import React, { useState, useEffect } from 'react';
import { 
  Receipt, 
  Truck, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Search, 
  Filter, 
  Plus, 
  Download, 
  ExternalLink, 
  RefreshCw, 
  Settings, 
  Eye,
  ShieldCheck,
  Ban
} from 'lucide-react';
import { DocumentoFiscal, EmpresaFiscal } from '../types/fiscal';
import { fiscalService } from '../services/fiscalService';
import { EmitirCteModal } from '../components/fiscal/EmitirCteModal';
import { EmitirNfseModal } from '../components/fiscal/EmitirNfseModal';
import { DetalhesDocumentoFiscalModal } from '../components/fiscal/DetalhesDocumentoFiscalModal';
import { Link } from 'react-router-dom';

export function FiscalPage() {
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);

  // Filtros
  const [modeloFiltro, setModeloFiltro] = useState<string>('todos');
  const [statusFiltro, setStatusFiltro] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Modais
  const [modalCteAberto, setModalCteAberto] = useState(false);
  const [modalNfseAberto, setModalNfseAberto] = useState(false);
  const [docSelecionado, setDocSelecionado] = useState<DocumentoFiscal | null>(null);

  // Empresas
  const [empresas, setEmpresas] = useState<EmpresaFiscal[]>([]);

  useEffect(() => {
    carregarEmpresas();
  }, []);

  useEffect(() => {
    carregarDocumentos();
  }, [page, modeloFiltro, statusFiltro]);

  const carregarEmpresas = async () => {
    const list = await fiscalService.getEmpresasFiscais();
    setEmpresas(list);
  };

  const carregarDocumentos = async () => {
    setLoading(true);
    try {
      const res = await fiscalService.getDocumentos({
        page,
        limit,
        modelo: modeloFiltro,
        status: statusFiltro,
        search: searchTerm.trim() || undefined,
      });
      setDocumentos(res.documentos);
      setTotal(res.total);
    } catch (e) {
      console.error('Erro ao carregar documentos fiscais:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    carregarDocumentos();
  };

  // Cálculos de métricas
  const contagemAutorizados = documentos.filter(d => d.status === 'autorizado').length;
  const contagemProcessando = documentos.filter(d => d.status === 'processando').length;
  const contagemRejeitados = documentos.filter(d => ['rejeitado', 'erro_autorizacao'].includes(d.status)).length;
  const contagemCancelados = documentos.filter(d => d.status === 'cancelado').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'autorizado':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            Autorizado
          </span>
        );
      case 'processando':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
            <Clock className="w-3 h-3 animate-spin text-amber-600" />
            Processando
          </span>
        );
      case 'cancelado':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-slate-200 text-slate-700">
            <Ban className="w-3 h-3 text-slate-500" />
            Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
            <AlertCircle className="w-3 h-3 text-rose-600" />
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Banner / Título */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center text-white font-bold shadow-md shadow-red-200">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                Módulo Fiscal (CT-e & NFS-e)
              </h1>
              <p className="text-xs text-slate-500">
                Emissão, transmissão SEFAZ e prefeituras, consulta e guarda de documentos fiscais
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Link
            to="/sistema?tab=fiscal"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
          >
            <Settings className="w-4 h-4 text-slate-500" />
            Configurações Fiscais
          </Link>

          <button
            onClick={() => setModalNfseAberto(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Emitir NFS-e
          </button>

          <button
            onClick={() => setModalCteAberto(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 shadow-sm shadow-red-200 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Emitir CT-e (OS / Carga)
          </button>
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-2xs font-semibold uppercase text-slate-500 block mb-1">
            Total Emitidos
          </span>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-black text-slate-900">{total}</span>
            <FileText className="w-5 h-5 text-slate-400" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-2xs font-semibold uppercase text-emerald-600 block mb-1">
            Autorizados
          </span>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-black text-emerald-700">{contagemAutorizados}</span>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-2xs font-semibold uppercase text-amber-600 block mb-1">
            Em Processamento
          </span>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-black text-amber-700">{contagemProcessando}</span>
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-2xs font-semibold uppercase text-rose-600 block mb-1">
            Cancelados / Rejeitados
          </span>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-black text-rose-700">{contagemCancelados + contagemRejeitados}</span>
            <Ban className="w-5 h-5 text-rose-500" />
          </div>
        </div>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Filtro por Modelo */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto">
          <button
            onClick={() => { setModeloFiltro('todos'); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
              modeloFiltro === 'todos'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todos os Modelos
          </button>
          <button
            onClick={() => { setModeloFiltro('57'); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
              modeloFiltro === '57'
                ? 'bg-red-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Truck className="w-3.5 h-3.5" />
            CT-e (Mod. 57)
          </button>
          <button
            onClick={() => { setModeloFiltro('NFS-e'); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
              modeloFiltro === 'NFS-e'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Receipt className="w-3.5 h-3.5" />
            NFS-e (Serviços)
          </button>
        </div>

        {/* Busca e Status */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <select
            value={statusFiltro}
            onChange={(e) => { setStatusFiltro(e.target.value); setPage(1); }}
            className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-700"
          >
            <option value="todos">Todos os Status</option>
            <option value="autorizado">Autorizado</option>
            <option value="processando">Processando</option>
            <option value="rejeitado">Rejeitado</option>
            <option value="cancelado">Cancelado</option>
          </select>

          <form onSubmit={handleSearchSubmit} className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Buscar chave, ref, motivo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-300 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-red-500"
            />
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
          </form>

          <button
            type="button"
            onClick={carregarDocumentos}
            className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 cursor-pointer"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabela de Documentos Fiscais */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-700">
            <thead className="bg-slate-50 border-b border-slate-200 text-2xs uppercase text-slate-500 font-semibold">
              <tr>
                <th className="px-4 py-3">Doc / Modelo</th>
                <th className="px-4 py-3">Referência</th>
                <th className="px-4 py-3">Empresa Emissora</th>
                <th className="px-4 py-3">Data / Hora</th>
                <th className="px-4 py-3">Valor Total</th>
                <th className="px-4 py-3">Status SEFAZ</th>
                <th className="px-4 py-3 text-right">Ações Fiscais</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-red-500" />
                    Carregando documentos fiscais...
                  </td>
                </tr>
              ) : documentos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="font-semibold text-slate-600">Nenhum documento fiscal encontrado</p>
                    <p className="text-2xs text-slate-400 mt-0.5">
                      Utilize os botões acima para emitir um CT-e a partir de uma OS ou NFS-e a partir de uma Medição.
                    </p>
                  </td>
                </tr>
              ) : (
                documentos.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
                    {/* Modelo / Número */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-2xs font-mono font-bold ${
                            doc.modelo === '57' || doc.modelo === '67'
                              ? 'bg-red-100 text-red-800'
                              : doc.modelo === 'NFS-e'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {doc.modelo === '57' ? 'CT-e' : doc.modelo}
                        </span>
                        <div>
                          <span className="font-bold text-slate-900 block">
                            #{doc.numero || doc.id}
                          </span>
                          <span className="text-2xs text-slate-400">Série {doc.serie || '1'}</span>
                        </div>
                      </div>
                    </td>

                    {/* Provedor Ref / Chave */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-2xs font-semibold text-slate-800 block">
                        {doc.provedor_ref || '-'}
                      </span>
                      {doc.chave_acesso ? (
                        <span className="font-mono text-3xs text-slate-400 truncate max-w-[140px] block" title={doc.chave_acesso}>
                          {doc.chave_acesso.slice(0, 16)}...
                        </span>
                      ) : null}
                    </td>

                    {/* Empresa */}
                    <td className="px-4 py-3">
                      <span className="font-semibold text-slate-800 block truncate max-w-[160px]">
                        {doc.empresa?.razao_social || `Empresa #${doc.empresa_id}`}
                      </span>
                      <span className="text-3xs uppercase font-mono text-slate-400">
                        Ambiente: {doc.ambiente}
                      </span>
                    </td>

                    {/* Data */}
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(doc.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>

                    {/* Valor */}
                    <td className="px-4 py-3 font-bold text-slate-900">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        doc.valor_total || 0
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {getStatusBadge(doc.status)}
                    </td>

                    {/* Ações */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Botão DACTE/DANFSE PDF */}
                        {doc.pdf_url && (
                          <a
                            href={doc.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                            title="Visualizar DACTE/PDF"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </a>
                        )}

                        {/* Botão Download XML */}
                        {doc.xml_url && (
                          <a
                            href={doc.xml_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                            title="Baixar XML Assinado"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        )}

                        {/* Botão Ver Detalhes */}
                        <button
                          onClick={() => setDocSelecionado(doc)}
                          className="p-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors cursor-pointer"
                          title="Detalhes e Cancelamento"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <span>
            Mostrando {documentos.length} de {total} documentos
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded bg-white border border-slate-300 text-slate-700 disabled:opacity-40 cursor-pointer"
            >
              Anterior
            </button>
            <span className="font-semibold text-slate-800">Página {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={documentos.length < limit}
              className="px-3 py-1 rounded bg-white border border-slate-300 text-slate-700 disabled:opacity-40 cursor-pointer"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      {/* Modais */}
      <EmitirCteModal
        isOpen={modalCteAberto}
        onClose={() => setModalCteAberto(false)}
        onSuccess={() => {
          carregarDocumentos();
        }}
      />

      <EmitirNfseModal
        isOpen={modalNfseAberto}
        onClose={() => setModalNfseAberto(false)}
        onSuccess={() => {
          carregarDocumentos();
        }}
      />

      <DetalhesDocumentoFiscalModal
        documento={docSelecionado}
        isOpen={!!docSelecionado}
        onClose={() => setDocSelecionado(null)}
        onUpdate={() => {
          carregarDocumentos();
          if (docSelecionado) {
            fiscalService.getDocumentoById(docSelecionado.id).then(setDocSelecionado);
          }
        }}
      />
    </div>
  );
}
